import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { useHistory } from 'react-router-dom';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { Page, LayoutSideNavigation, Modal } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import SectionUserInfo from './Sections/SectionUserInfo';
import SectionYachtClubAffiliations from './Sections/SectionYachtClubAffiliations';
import SectionHomePort from './Sections/SectionHomePort';
import SectionMyVessel from './Sections/SectionMyVessel';
import SectionMemberStats from './Sections/SectionMemberStats';
import SectionMyListings from './Sections/SectionMyListings';
import css from './ProfilePage.module.css';
import listingCss from '../ListingPage/ListingPage.module.css';
import InquiryForm from '../ListingPage/InquiryForm/InquiryForm';
import { createResourceLocatorString } from '../../util/routes';
import { sendInquiry } from '../ListingPage/ListingPage.duck';
import { onTriggerError } from '../../util/toast';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { fetchCheckIns } from '../../util/api';
import { updateProfile, uploadImage } from '../ProfileSettingsPage/ProfileSettingsPage.duck';

const getPublicProfileCheckIns = publicData => {
  const checkIns =
    publicData?.onbuoyCheckIns || publicData?.publicMoorageCheckIns || publicData?.checkIns || [];

  return Array.isArray(checkIns) ? checkIns : [];
};

export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const {
    scrollingDisabled,
    currentUser,
    userShowError,
    useCurrentUser,
    user,
    reviews,
    listings,
    onManageDisableScrolling,
    onSendInquiry,
    onUpdateProfile,
    onImageUpload,
    updateInProgress,
    updateProfileError,
    uploadImageError,
    uploadInProgress,
    sendInquiryInProgress,
    sendInquiryError,
  } = props;
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const profileUser = ensureUser(useCurrentUser ? currentUser : user);
  const isCurrentUser =
    ensuredCurrentUser.id && profileUser.id && ensuredCurrentUser.id.uuid === profileUser.id.uuid;
  const { bio, displayName, publicData } = profileUser?.attributes?.profile || {};
  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);
  const [checkInHistory, setCheckInHistory] = useState([]);
  const [checkInHistoryLoading, setCheckInHistoryLoading] = useState(false);
  const [checkInHistoryError, setCheckInHistoryError] = useState(null);

  useEffect(() => {
    if (!isCurrentUser) {
      setCheckInHistory([]);
      return;
    }

    let cancelled = false;
    setCheckInHistoryLoading(true);
    setCheckInHistoryError(null);

    fetchCheckIns({ status: 'all', perPage: 10 })
      .then(response => {
        if (cancelled) return;
        setCheckInHistory(Array.isArray(response?.checkIns) ? response.checkIns : []);
      })
      .catch(error => {
        if (cancelled) return;
        setCheckInHistoryError(error);
      })
      .finally(() => {
        if (!cancelled) setCheckInHistoryLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isCurrentUser]);

  const onCloseInquiryModal = () => setIsInquiryModalOpen(false);

  const onSubmitInquiry = async values => {
    try {
      const { listingId, message } = values;
      const listing = listings?.find(item => item?.id?.uuid === listingId);

      const txId = await onSendInquiry(listing, message.trim());
      setIsInquiryModalOpen(false);
      history.push(
        createResourceLocatorString('OrderDetailsPage', routeConfiguration, { id: txId.uuid }, {})
      );
    } catch (error) {
      onTriggerError('Something went wrong while sending inquiry.');
    }
  };

  const listingOptions = listings?.map(l => ({
    key: l?.id?.uuid,
    label: l?.attributes?.title,
  }));
  const visibleCheckIns = isCurrentUser ? checkInHistory : getPublicProfileCheckIns(publicData);
  const visibleCheckInsLoading = isCurrentUser ? checkInHistoryLoading : false;
  const visibleCheckInsError = isCurrentUser ? checkInHistoryError : null;

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        name: schemaTitle,
      }}
    >
      <LayoutSideNavigation
        sideNavClassName={css.aside}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
        isProfilePage={true}
      >
        <SectionUserInfo
          user={profileUser}
          displayName={displayName}
          editable={isCurrentUser}
          bio={bio}
          onUpdateProfile={onUpdateProfile}
          onImageUpload={onImageUpload}
          updateInProgress={updateInProgress}
          updateProfileError={updateProfileError}
          uploadImageError={uploadImageError}
          uploadInProgress={uploadInProgress}
        />{' '}
        <div className={css.profileContent}>
          <SectionYachtClubAffiliations
            publicData={publicData}
            intl={intl}
            editable={isCurrentUser}
            onUpdateProfile={onUpdateProfile}
            updateInProgress={updateInProgress}
          />
          <SectionHomePort
            publicData={publicData}
            editable={isCurrentUser}
            onUpdateProfile={onUpdateProfile}
            updateInProgress={updateInProgress}
          />
          <SectionMyVessel publicData={publicData} intl={intl} editable={isCurrentUser} />
          <SectionMemberStats reviews={reviews} checkIns={visibleCheckIns} intl={intl} />
          <SectionMyListings
            listings={listings}
            reviews={reviews}
            checkIns={visibleCheckIns}
            checkInsLoading={visibleCheckInsLoading}
            checkInsError={visibleCheckInsError}
            intl={intl}
          />
        </div>
      </LayoutSideNavigation>
      <Modal
        id="ProfilePage.inquiry"
        contentClassName={listingCss.inquiryModalContent}
        isOpen={isInquiryModalOpen}
        onClose={onCloseInquiryModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <InquiryForm
          className={listingCss.inquiryForm}
          submitButtonWrapperClassName={listingCss.inquirySubmitButtonWrapper}
          listingTitle={`${displayName}'s profile`}
          authorDisplayName={displayName}
          sendInquiryError={sendInquiryError}
          onSubmit={onSubmitInquiry}
          inProgress={sendInquiryInProgress}
          listingOptions={listingOptions}
          fromProfilePage={true}
        />
      </Modal>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews = [],
    queryReviewsError,
  } = state.ProfilePage;
  const { sendInquiryInProgress, sendInquiryError } = state.ListingPage;
  const {
    updateInProgress,
    updateProfileError,
    uploadImageError,
    uploadInProgress,
  } = state.ProfileSettingsPage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    reviews,
    queryReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    updateInProgress,
    updateProfileError,
    uploadImageError,
    uploadInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onImageUpload: data => dispatch(uploadImage(data)),
  onUpdateProfile: data => dispatch(updateProfile(data)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ProfilePage = compose(connect(mapStateToProps, mapDispatchToProps))(ProfilePageComponent);

export default ProfilePage;
